// Copyright (c) Zammo Inc.
import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '@iconify/react';
import arrowDown from '@iconify/icons-simple-line-icons/arrow-down';

import { palette } from '../../styles/variables';
import media from '../../styles/media';

const Container = styled.div`
  position: relative;
`;

interface ButtonProps {
  size?: string;
}
const Button = styled.button<ButtonProps>`
  font-size: ${(props) => (props.size === 'small' ? 'small' : '20px')};
  font-weight: 400;
  color: ${palette.black};
  height: ${(props) => (props.size === 'small' ? '20%' : '46px')};
  padding: 8px 20px;
  background: ${palette.lightGray};
  width: 100%;
  border: none;
  border-radius: 5px;
  display: block;
  position: relative;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  svg {
    display: block;
    font-size: ${(props) => (props.size === 'small' ? 'small' : '20px')};
  }
`;

const IconContainer = styled.div`
  color: ${palette.blue};
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
`;

const Options = styled.div`
  position: absolute;
  left: 0;
  top: calc(100% + 5px);
  width: 100%;
  padding: 0;
  background-color: white;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.25rem;
  z-index: 3;

  max-height: 200px;
  overflow: auto;
`;

const InputContainer = styled.div`
  font-size: 20px;
  font-weight: 400;
  color: ${palette.black};
  padding: 8px 20px;
  background: ${palette.lightGray};
  width: 100%;
  border: none;
  text-align: left;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  ${media.xlg`
    font-size: 1rem;
  `}

  input {
    flex: 1;
    padding-right: 30px;
    height: 30px;
    background-color: transparent;
    width: 100%;
  }

  svg {
    color: ${palette.blue};
    font-size: 20px;
  }
`;

type OptionProps = {
  selected: boolean;
  size?: string;
};

const Option = styled.div<OptionProps>`
  font-size: ${(props) => (props.size === 'small' ? 'small' : '16px')};
  color: ${palette.black};
  padding: 5px 20px;
  transition: all 0.5s ease-in-out 0s;
  cursor: pointer;
  &:hover {
    background: ${palette.blue};
    color: white;
  }
  ${(props) =>
    props.selected &&
    `
    background: ${palette.blue};
    color: white;
  `}
`;

export type Option = {
  value: string;
  label: string;
};

type Props = {
  placeholder: string;
  options: Option[];
  onClick: (item: Option) => void;
  selectedValue?: string;
  size?: string;
  disabled?: boolean;
  search?: boolean;
};

const Select = ({ placeholder, options, onClick, selectedValue, size, disabled, search, ...props }: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [label, setLabel] = useState('');
  const [originalLabel, setOriginalLabel] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const [touched, setTouched] = useState(false);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      showDropdown &&
      wrapperRef.current &&
      event.target instanceof Node &&
      !wrapperRef.current.contains(event.target)
    ) {
      setShowDropdown(false);
      setLabel(originalLabel);
      setTouched(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  useEffect(() => {
    if (search) {
      if (!touched) {
        setFilteredOptions(options);
      } else {
        const newOptions = options.filter((o) => !label || o.label.toLowerCase().includes(label.toLowerCase()));
        setFilteredOptions(newOptions);
      }
    }
  }, [label, options, search, touched]);

  useEffect(() => {
    const matched = options.find((option) => option.value === selectedValue);
    if (matched) {
      setLabel(matched.label);
      setOriginalLabel(matched.label);
    } else {
      if (!search) {
        setLabel(placeholder);
        setOriginalLabel(placeholder);
      }
    }
  }, [placeholder, search, selectedValue, options]);

  const handleButtonClick = () => {
    if (showDropdown && search) {
      setLabel(originalLabel);
      setTouched(false);
    }
    setShowDropdown(!showDropdown);
  };

  const handleOptionClick = (item: Option) => () => {
    onClick(item);
    setShowDropdown(false);
    setTouched(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLabel(e.target.value);
    setTouched(true);
    setShowDropdown(true);
  };

  return (
    <Container ref={wrapperRef} {...props}>
      {search ? (
        <InputContainer>
          <input type="text" placeholder={placeholder} value={label} onChange={handleInputChange} />
          <div aria-haspopup="true" aria-expanded="false" onClick={handleButtonClick}>
            <Icon icon={arrowDown} />
          </div>
        </InputContainer>
      ) : (
        <Button type="button" aria-haspopup="true" aria-expanded="false" onClick={handleButtonClick} size={size}>
          <span>{label}</span>
          <IconContainer>
            <Icon icon={arrowDown} />
          </IconContainer>
        </Button>
      )}
      {!disabled && showDropdown && (
        <Options>
          {filteredOptions.map((option) => (
            <Option
              key={option.value}
              onClick={handleOptionClick(option)}
              selected={selectedValue === option.value}
              size={size}
            >
              {option.label}
            </Option>
          ))}
        </Options>
      )}
    </Container>
  );
};

export default Select;
